import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Download, Share2, Sparkles, Gift } from "lucide-react";
import { CATEGORY_LABELS, absoluteUrl } from "./Avvento";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AvventoGiorno() {
  const { daySlug } = useParams();
  // URL formato /calendario-avvento-2026/giorno-N
  const day = (daySlug?.match(/^giorno-(\d{1,2})$/) || [])[1] || "";
  const [doc, setDoc] = useState(null);
  const [notAvailable, setNotAvailable] = useState(false);

  useEffect(() => {
    if (!day) { setNotAvailable(true); return; }
    axios.get(`${API}/advent/day/${day}`)
      .then((r) => {
        setDoc(r.data);
        document.title = `Giorno ${day} · ${r.data.title || ""} · Calendario Avvento`;
      })
      .catch(() => setNotAvailable(true));
  }, [day]);

  const share = async () => {
    try {
      await navigator.share?.({
        title: `Giorno ${day} · ${doc?.title || "Avvento"}`,
        text: doc?.intro || "",
        url: window.location.href,
      });
    } catch {}
  };

  if (notAvailable) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-8 text-center">
        <Sparkles className="text-[#FF2768] mb-4" size={32} />
        <h1 className="font-display text-4xl text-[#3D2140] mb-2">Non ancora!</h1>
        <p className="text-[#3D2140]/60 mb-6">Questa casella non è ancora disponibile. Torna il giorno {day}.</p>
        <Link to="/calendario-avvento-2026" className="inline-flex items-center gap-2 text-[#FF2768] underline">
          <ArrowLeft size={14} /> Torna al calendario
        </Link>
      </div>
    );
  }
  if (!doc) return <div className="min-h-[60vh] flex items-center justify-center text-[#3D2140]/50">Un attimo…</div>;

  // Suggerimento voucher per i giorni 6, 13, 20
  const showVoucherNudge = [6, 13, 20].includes(Number(day));

  return (
    <article className="min-h-screen bg-[#FAF8F5] pb-20" data-testid="avvento-day-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10 pt-12 md:pt-20">
        <Link to="/calendario-avvento-2026" className="inline-flex items-center gap-2 text-sm text-[#3D2140]/60 hover:text-[#FF2768] mb-8" data-testid="avvento-back">
          <ArrowLeft size={14} /> Torna al calendario
        </Link>

        {doc.theme_image_url ? (
          <>
            {/* Banner coordinato: illustrazione integrale dell'ambientazione + numero e titolo sovrapposti */}
            <div className="relative max-w-sm mx-auto rounded-3xl overflow-hidden shadow-lg mb-4" data-testid="avvento-day-banner">
              <img src={absoluteUrl(doc.theme_image_url)} alt="" className="w-full h-auto block" />
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 px-6">
                <span className="shrink-0 w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#FAF8F5]/95 text-[#3D2140] flex items-center justify-center font-display text-3xl md:text-4xl shadow-md">
                  {doc.day}
                </span>
                <span className="bg-[#FAF8F5]/92 rounded-xl px-3 py-1.5 shadow-sm max-w-full">
                  <h1 className="font-display text-base md:text-xl leading-tight text-[#3D2140] text-center">{doc.title}</h1>
                </span>
              </div>
            </div>
            <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-semibold mb-8">
              {CATEGORY_LABELS[doc.category] || "Avvento 2026"}
              {doc.age_range && <span className="text-[#3D2140]/60 normal-case tracking-normal font-normal"> · Età: <strong>{doc.age_range}</strong></span>}
            </p>
          </>
        ) : (
          <div className="flex items-start gap-6 mb-8">
            <div className="shrink-0 w-20 h-20 md:w-24 md:h-24 rounded-2xl bg-[#FF2768] text-white flex items-center justify-center font-display text-5xl md:text-6xl">
              {doc.day}
            </div>
            <div className="flex-1 pt-1">
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF2768] font-semibold mb-2">
                {CATEGORY_LABELS[doc.category] || "Avvento 2026"}
              </p>
              <h1 className="font-display text-3xl md:text-5xl leading-[1.05] text-[#3D2140]">{doc.title}</h1>
              {doc.age_range && <p className="text-sm text-[#3D2140]/60 mt-2">Età: <strong>{doc.age_range}</strong></p>}
            </div>
          </div>
        )}

        {doc.image_url && (
          <img src={doc.image_url} alt={doc.title} className="w-full rounded-2xl mb-8" data-testid="avvento-day-image" />
        )}

        {doc.intro && <p className="font-editorial italic text-xl text-[#3D2140]/80 mb-6 leading-relaxed">{doc.intro}</p>}

        {doc.description && (
          <div className="prose prose-lg max-w-none text-[#3D2140]/90 whitespace-pre-line mb-6">{doc.description}</div>
        )}

        {doc.materials && (
          <div className="bg-white rounded-2xl p-6 mb-5 border border-[#3D2140]/10">
            <h3 className="text-sm uppercase tracking-widest text-[#FF2768] font-semibold mb-2">Materiali</h3>
            <p className="text-[#3D2140]/80 whitespace-pre-line">{doc.materials}</p>
          </div>
        )}

        {doc.variants && (
          <div className="bg-white rounded-2xl p-6 mb-5 border border-[#3D2140]/10">
            <h3 className="text-sm uppercase tracking-widest text-[#FF2768] font-semibold mb-2">Varianti</h3>
            <p className="text-[#3D2140]/80 whitespace-pre-line">{doc.variants}</p>
          </div>
        )}

        {doc.educational_value && (
          <div className="bg-[#81C3C9]/20 rounded-2xl p-6 mb-5">
            <h3 className="text-sm uppercase tracking-widest text-[#3D2140] font-semibold mb-2">Il valore educativo</h3>
            <p className="text-[#3D2140]/85 whitespace-pre-line">{doc.educational_value}</p>
          </div>
        )}

        {doc.safety_notes && (
          <div className="bg-[#FFE45C]/30 rounded-2xl p-6 mb-5">
            <h3 className="text-sm uppercase tracking-widest text-[#3D2140] font-semibold mb-2">⚠ Attenzioni per la sicurezza</h3>
            <p className="text-[#3D2140]/85 whitespace-pre-line">{doc.safety_notes}</p>
          </div>
        )}

        <div className="flex flex-wrap gap-3 mt-8">
          {doc.pdf_url && (
            <a href={doc.pdf_url} target="_blank" rel="noopener noreferrer" data-testid="avvento-pdf" className="inline-flex items-center gap-2 px-5 py-3 rounded-full bg-[#FF2768] text-white text-sm font-semibold hover:bg-[#c81e52]">
              <Download size={16} /> Scarica il PDF
            </a>
          )}
          <button onClick={share} data-testid="avvento-share" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#3D2140]/20 text-[#3D2140] text-sm font-semibold hover:bg-[#3D2140] hover:text-white">
            <Share2 size={16} /> Condividi
          </button>
          <Link to="/calendario-avvento-2026" className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-[#3D2140]/20 text-[#3D2140] text-sm font-semibold hover:bg-[#3D2140] hover:text-white">
            <ArrowLeft size={16} /> Al calendario
          </Link>
        </div>

        {showVoucherNudge && (
          <div className="mt-12 bg-white rounded-2xl p-6 border-2 border-[#FF2768]/30">
            <div className="flex items-start gap-3">
              <Gift className="text-[#FF2768] shrink-0 mt-1" size={20} />
              <div>
                <p className="font-display text-xl text-[#3D2140] mb-1">Un regalo diverso, pensato per accompagnare chi si prende cura</p>
                <p className="text-sm text-[#3D2140]/70 mb-3">Quest'anno puoi regalare anche tempo, ascolto e cura.</p>
                <Link to="/regala-cura" className="text-sm text-[#FF2768] font-semibold underline">
                  Scopri i Voucher di Cura →
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </article>
  );
}
